<template>
  <section>
    <spinner v-if="isLoading"></spinner>
    <errors v-if="isVisibleErrorsModal"></errors>

    <div class="content-wrapper">
      <div class="content-heading">Communication preferences</div>

      <div class="panel panel-default">
        <dismissable-alert
          title="Communication preference updated!"
          :closeAction="hideCommunicationUpdatedAlert"
          :isShowing="communicationUpdated"
        />

        <div class="panel-title top-margin">Choose your communication preferences</div>
        <div class="line-separator"></div>

        <button
          type="button"
          class="btn btn-default btn-lg top-margin bottom-margin"
          :disabled="isReadOnly || allEmail"
          @click="setAllToEmail"
        >
          Set All Delivery Methods to Email
        </button>

        <div class="table-responsive bottom-margin">
          <table class="table table-striped">
            <thead>
              <tr>
                <th class="text-left">Document</th>
                <th class="text-center delivery-col">Email</th>
                <th class="text-center delivery-col">Postal Mail</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-left">Transaction Notices &amp; Options Forms</td>
                <td class="text-center">
                  <div class="radio c-radio">
                    <label>
                      <input
                        type="radio"
                        name="transactions"
                        :checked="transactionsEmail"
                        :disabled="isReadOnly"
                        @click.prevent="setExclusivePreference('TRANSACTIONS', 'EMAIL')"
                      />
                      <span class="fa fa-circle"></span>
                    </label>
                  </div>
                </td>
                <td class="text-center">
                  <div class="radio c-radio">
                    <label>
                      <input
                        type="radio"
                        name="transactions"
                        :checked="transactionsPostal"
                        :disabled="isReadOnly"
                        @click.prevent="setExclusivePreference('TRANSACTIONS', 'SNAIL-MAIL')"
                      />
                      <span class="fa fa-circle"></span>
                    </label>
                  </div>
                </td>
              </tr>
              <tr>
                <td class="text-left">Impact Report (annual each fall)</td>
                <td class="text-center">
                  <div class="radio c-radio">
                    <label>
                      <input
                        type="radio"
                        name="impactReport"
                        :checked="impactReportEmail"
                        :disabled="isReadOnly"
                        @click.prevent="setExclusivePreference('IMPACT_REPORT', 'EMAIL')"
                      />
                      <span class="fa fa-circle"></span>
                    </label>
                  </div>
                </td>
                <td class="text-center">
                  <div class="radio c-radio">
                    <label>
                      <input
                        type="radio"
                        name="impactReport"
                        :checked="impactReportPostal"
                        :disabled="isReadOnly"
                        @click.prevent="setExclusivePreference('IMPACT_REPORT', 'SNAIL-MAIL')"
                      />
                      <span class="fa fa-circle"></span>
                    </label>
                  </div>
                </td>
              </tr>
              <tr>
                <td class="text-left">
                  1099 Tax Form (annual each January, can select both email &amp; postal)
                </td>
                <td class="text-center">
                  <div class="checkbox c-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        :checked="form1099Email"
                        :disabled="isReadOnly"
                        @click.prevent="toggle1099('EMAIL')"
                      />
                      <span class="fa fa-check"></span>
                    </label>
                  </div>
                </td>
                <td class="text-center">
                  <div class="checkbox c-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        :checked="form1099Postal"
                        :disabled="isReadOnly"
                        @click.prevent="toggle1099('SNAIL-MAIL')"
                      />
                      <span class="fa fa-check"></span>
                    </label>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="footnote">
          All of the above, as well as Monthly Statements and other Reports are always
          available in your Documents section online.
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';

import Errors from '@/components/modals/Errors';
import Spinner from '@/components/widgets/Spinner';
import DismissableAlert from '@/components/widgets/DismissableAlert';
import {
  SET_ERRORS_MODAL_INFO,
  SHOW_ERRORS_MODAL,
} from '@/store/mutation-types';

const EMAIL = 'EMAIL';
const POSTAL = 'SNAIL-MAIL';
const BOTH = 'BOTH';

export default {
  data() {
    return {
      communicationUpdated: false,
      isLoading: true,
      communicationPreference: EMAIL,
      communication1099Preference: EMAIL,
      communicationImpactReportPreference: EMAIL,
    };
  },
  beforeMount() {
    this.initialize();
  },
  methods: {
    ...mapActions('account', [
      'getAccount',
      'updateAccount',
    ]),
    ...mapMutations('errors', {
      showErrorsModal: SHOW_ERRORS_MODAL,
      setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
    }),
    async initialize() {
      this.isLoading = true;
      const account = await this.getAccount(this.activeAccountId);
      this.communicationPreference = account.communicationPreference || EMAIL;
      this.communication1099Preference = account.communication1099Preference || EMAIL;
      this.communicationImpactReportPreference =
        account.communicationImpactReportPreference || EMAIL;
      this.isLoading = false;
    },
    async persistPreferences(payload) {
      if (this.isReadOnly) return;
      try {
        this.isLoading = true;
        await this.updateAccount({
          accountId: this.activeAccountId,
          payload,
        });
        await this.initialize();
        this.communicationUpdated = true;
      } catch (e) {
        this.setErrorsModalInfo(['Something went wrong. Please try again.']);
        this.showErrorsModal();
      } finally {
        this.isLoading = false;
      }
    },
    setExclusivePreference(category, value) {
      if (this.isReadOnly) return;
      if (category === 'TRANSACTIONS') {
        if (this.communicationPreference === value) return;
        this.persistPreferences({ communicationPreference: value });
      } else if (category === 'IMPACT_REPORT') {
        if (this.communicationImpactReportPreference === value) return;
        this.persistPreferences({ communicationImpactReportPreference: value });
      }
    },
    toggle1099(value) {
      if (this.isReadOnly) return;
      let next;
      if (value === EMAIL) {
        // unchecking email leaves postal; checking it adds to whatever is set
        next = this.form1099Email ? POSTAL : BOTH;
      } else {
        // unchecking postal leaves email; checking it adds to whatever is set
        next = this.form1099Postal ? EMAIL : BOTH;
      }
      this.persistPreferences({ communication1099Preference: next });
    },
    setAllToEmail() {
      if (this.isReadOnly || this.allEmail) return;
      this.persistPreferences({
        communicationPreference: EMAIL,
        communication1099Preference: EMAIL,
        communicationImpactReportPreference: EMAIL,
      });
    },
    hideCommunicationUpdatedAlert() {
      this.communicationUpdated = false;
    },
  },
  computed: {
    ...mapState('errors', ['isVisibleErrorsModal']),
    ...mapState('app', ['activeAccountId', 'user']),
    transactionsEmail() {
      return this.communicationPreference === EMAIL;
    },
    transactionsPostal() {
      return this.communicationPreference === POSTAL;
    },
    impactReportEmail() {
      return this.communicationImpactReportPreference === EMAIL;
    },
    impactReportPostal() {
      return this.communicationImpactReportPreference === POSTAL;
    },
    form1099Email() {
      return this.communication1099Preference === EMAIL
        || this.communication1099Preference === BOTH;
    },
    form1099Postal() {
      return this.communication1099Preference === POSTAL
        || this.communication1099Preference === BOTH;
    },
    allEmail() {
      return this.communicationPreference === EMAIL
        && this.communication1099Preference === EMAIL
        && this.communicationImpactReportPreference === EMAIL;
    },
    isReadOnly() {
      return this.user && this.user.userType === 'READ_ONLY';
    },
  },
  components: {
    errors: Errors,
    spinner: Spinner,
    'dismissable-alert': DismissableAlert,
  },
};
</script>

<style scoped>
.panel-default {
  padding: 30px 40px;
}

.panel-title {
  font-size: 20px;
  margin-bottom: 5px;
}

.line-separator {
  padding: 8px;
  line-height: 1.52857143;
  vertical-align: top;
  border-top: 1px solid #eee;
}

table {
  border-collapse: collapse;
}

th {
  padding: 14px 8px;
  color: #888;
  border-bottom-width: 1px;
  vertical-align: bottom;
  border-bottom: 1px solid #eee;
  border-top: 0;
  line-height: 1.52857143;
  font-weight: normal;
}

td {
  padding: 16px 8px;
  line-height: 1.52857143;
  vertical-align: middle;
  border-top: 1px solid #eee;
  font-size: 15px;
}

.table-striped > tbody > tr:nth-of-type(odd) {
  background-color: #fafbfc;
}

.delivery-col {
  width: 18%;
}

.checkbox {
  margin: 0;
  display: inline-block;
  min-height: 0;
}

.checkbox label {
  padding-left: 20px;
  margin-bottom: 0;
}

.footnote {
  margin-top: 8px;
  font-size: 14px;
  color: #6d6d6d;
}

.top-margin {
  margin-top: 2%;
}

.bottom-margin {
  margin-bottom: 2%;
}
</style>



// WEBPACK FOOTER //
// InvestorCommunicationPreferences.vue?7c830e9f